// ListingCard.jsx
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  IconSpinner,
  AvatarMedium,
  AvatarSmall,
  SecondaryButton,
  PrimaryButton,
} from '../../components';

import css from './ListingCard.module.css';
import Icons from '../Icons/Icons';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { getWishlist, isArrayLength } from '../../util/dataExtractors';
import { currentUserSelector } from '../../ducks/user.duck';
import { useDispatch, useSelector } from 'react-redux';
import IconHeartFilled from '../IconHeartFilled/IconHeartFilled';
import IconHeart from '../IconHeart/IconHeart';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { capitalizeFirst, getEnumLabels } from '../../util/genericHelpers';
const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const getNumber = (...values) => {
  const found = values.find(value => value !== null && value !== undefined && value !== '');
  const number = Number(found);
  return Number.isFinite(number) ? number : null;
};

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) return null;

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
    isSeller,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
      {isSeller && <div className={css.bestSeller}>Indian</div>}
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    onToggleWishlistModal,
    isSearchPage,
    user,
  } = props;
  const currentUser = useSelector(currentUserSelector);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const wishlistItems = getWishlist(currentUser);
  const combinedWishlistItems = isArrayLength(wishlistItems) && wishlistItems.flatMap(w => w.ids);

  const isAlreadyWishlisted =
    isArrayLength(combinedWishlistItems) &&
    combinedWishlistItems.find(id => id === currentListing?.id?.uuid);
  const classes = classNames(
    rootClassName || css.root,
    className,
    isSearchPage ? css.searchListingCard : null
  );

  const {
    title = '',
    description = '',
    publicData,
    price,
    metadata = {},
  } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const tagline = publicData?.tagline;
  const dietaryOptions = publicData?.dietaryOptions || [];
  const allergenHandling = publicData?.allergenHandlingText || [];

  // Format enum values to display labels
  const formattedDietaryOptions = getEnumLabels(
    config?.listing?.listingFields,
    'dietaryOptions',
    dietaryOptions
  );

  const combinedPills = [
    ...formattedDietaryOptions,
    ...(Array.isArray(allergenHandling) ? allergenHandling : []),
  ].map(capitalizeFirst);
  const minGuests = publicData?.minGuests || 4;
  const maxGuests = publicData?.maxGuests || 10;

  // Format guest labels using enum helper
  const formattedMinGuests =
    getEnumLabels(config?.listing?.listingFields, 'minGuests', [minGuests])[0] || minGuests;

  const formattedMaxGuests =
    getEnumLabels(config?.listing?.listingFields, 'maxGuests', [maxGuests])[0] || maxGuests;

  const guestRangeText = `Ideal for ${formattedMinGuests}-${formattedMaxGuests} guests`;
  const { geolocation } = currentListing.attributes;
  const address = publicData?.location?.address || geolocation?.address || 'Location not set';
  const serviceRadius = publicData?.serviceRadius || '8';
  const addressText = `${address} • Up to ${serviceRadius} km`;

  // Format primary cuisine labels
  const primaryCuisine = publicData?.primaryCuisine || [];
  const formattedPrimaryCuisine = getEnumLabels(
    config?.listing?.listingFields,
    'primaryCuisine',
    primaryCuisine
  );
  const cuisineText =
    formattedPrimaryCuisine.length > 0 ? `${formattedPrimaryCuisine.join(', ')} Cuisine` : null;
  const rating = getNumber(
    publicData?.rating,
    publicData?.averageRating,
    publicData?.customerRating,
    metadata?.rating,
    metadata?.averageRating,
    metadata?.customerRating
  );
  const reviewCount = getNumber(
    publicData?.reviewCount,
    publicData?.reviewsCount,
    publicData?.numberOfReviews,
    metadata?.reviewCount,
    metadata?.reviewsCount,
    metadata?.numberOfReviews
  );
  const hasRating = rating !== null;
  const formattedRating = hasRating ? rating.toFixed(1).replace(/\.0$/, '') : null;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const onRemoveWishlistedItem = itemId => {
    // Find the index of the wishlist that contains the item ID
    const wishlistIndex = wishlistItems.findIndex(wishlist => wishlist?.ids?.includes(itemId));

    if (wishlistIndex === -1) {
      console.error('Item not found in any wishlist');
      return;
    }

    // Create a new array without the item ID
    const updatedIds = wishlistItems[wishlistIndex].ids.filter(id => id !== itemId);

    const updatedWishlistItems = wishlistItems.map((item, index) =>
      index === wishlistIndex ? { ...item, ids: updatedIds } : item
    );

    dispatch(
      updateProfile({
        protectedData: {
          wishlist: updatedWishlistItems,
        },
      })
    );
  };
  return (
    <>
      {isSearchPage ? (
        <div className={classes}>
          <NamedLink className={css.cardLink} name="ListingPage" params={{ id, slug }}>
            <div className={css.profileSec}>
              <ListingCardImage
                renderSizes={renderSizes}
                title={title}
                currentListing={currentListing}
                config={config}
                setActivePropsMaybe={setActivePropsMaybe}
                aspectWidth={aspectWidth}
                aspectHeight={aspectHeight}
                variantPrefix={variantPrefix}
                style={cardStyle}
                showListingImage={showListingImage}
                isSeller={false}
              />
            </div>
            <div className={css.info}>
              <div className={css.mainInfo}>
                <div className={css.titleWrapper}>
                  <div className={css.titleSec}>
                    <div className={css.titleFavMain}>
                      <div className={css.title}>
                        {richText(title, {
                          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                          longWordClass: css.longWord,
                        })}
                      </div>
                    </div>

                    {/* {tagline && <p>{tagline}</p>} */}
                    {/* <div className={css.pills}>
                    {combinedPills.map((pill, index) => (
                      <span key={index} className={css.pill}>{pill}</span>
                    ))}
                  </div> */}
                    {/* <p className={css.location}>
                    <Icons name="location" />
                    {addressText}
                  </p> */}
                    {/* <p className={css.location}>
                    <Icons name="users" />
                    {guestRangeText}
                  </p> */}
                    <p>{cuisineText}</p>
                    {/* {showAuthorInfo ? (
                    <div className={css.authorInfo}>
                      <AvatarSmall user={user} />
                      <FormattedMessage id="ListingCard.author" values={{ authorName }} />
                    </div>
                  ) : null} */}
                    {/* <PriceMaybe
                    price={price}
                    publicData={publicData}
                    config={config}
                    intl={intl}
                    listingTypeConfig={foundListingTypeConfig}
                  /> */}
                    {/* cart UI left as-is/commented */}
                  </div>
                  {hasRating ? (
                    <div className={css.ratingSec}>
                      <Icons name="star" /> <strong>{formattedRating}</strong>
                      {reviewCount !== null ? <span>({reviewCount})</span> : null}
                    </div>
                  ) : null}
                  <div>
                    <p className={css.descriptionText}>{description}</p>
                  </div>
                </div>
                <div className={css.priceSec}>
                  {price ? (
                    <p className={css.price}>
                      <span className={css.priceAmount}>{formatMoney(intl, price)}</span>
                      <span className={css.priceUnit}>per guest</span>
                    </p>
                  ) : (
                    <p className={css.price}>Price not set</p>
                  )}
                  {/* <p className={css.totalGuests}>Minimum {formattedMinGuests} guests</p>
                <p className={css.hostQuantity}>Can host up to {formattedMaxGuests} guests</p> */}
                </div>
              </div>
              {/* <div className={css.viewSevices}>
              <div className={css.viewGallary}>
                {publicData?.gallery && publicData.gallery.length > 0 ? (
                  publicData.gallery.slice(0, 3).map((image, index) => {
                    // Handle different image data structures
                    const imageObj = image?.id ? image : null;
                    const imageUrl = image?.attributes?.variants?.['listing-card'] ||
                      image?.attributes?.url ||
                      image?.url ||
                      image;

                    return (
                      <div key={index} className={css.imgBlock}>
                        {imageObj && imageObj.attributes ? (
                          <ResponsiveImage
                            rootClassName={css.galleryImage}
                            alt={`${title} - Profile gallery image ${index + 1}`}
                            image={imageObj}
                            variants={['listing-card']}
                            sizes="(max-width: 767px) 100vw, 50vw"
                          />
                        ) : (
                          <img
                            src={imageUrl}
                            alt={`${title} - Profile gallery image ${index + 1}`}
                            className={css.galleryImage}
                          />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className={css.imgBlock}>
                    <div className={css.noImagePlaceholder}>No gallery images available</div>
                  </div>
                )}
              </div>
              <div className={css.viewActions}>
                <SecondaryButton>View Profile</SecondaryButton>
                <PrimaryButton>View Services</PrimaryButton>
              </div>
            </div> */}
            </div>
          </NamedLink>
          <button
            type="button"
            className={css.favorites}
            aria-label={isAlreadyWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              if (isAlreadyWishlisted) {
                onRemoveWishlistedItem(id);
              } else if (onToggleWishlistModal) {
                onToggleWishlistModal(id);
              }
            }}
          >
            {isAlreadyWishlisted ? <IconHeartFilled /> : <IconHeart />}
          </button>
        </div>
      ) : (
        <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
          <ListingCardImage
            renderSizes={renderSizes}
            title={title}
            currentListing={currentListing}
            config={config}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            style={cardStyle}
            showListingImage={showListingImage}
            isSeller={true}
          />

          <div className={css.info}>
            <div className={css.mainInfo}>
              <div className={css.titleWrapper}>
                {showListingImage && (
                  <div className={css.title}>
                    {richText(title, {
                      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                      longWordClass: css.longWord,
                    })}
                  </div>
                )}
              </div>
              {price ? (
                <p className={css.price}>{formatMoney(intl, price)}</p>
              ) : (
                <p className={css.price}>Price not set</p>
              )}
              {showAuthorInfo ? (
                <div className={css.authorInfo}>
                  <AvatarSmall user={user} disableProfileLink />
                  <FormattedMessage id="ListingCard.author" values={{ authorName }} />
                </div>
              ) : null}
            </div>

            {/* <PriceMaybe
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
        /> */}
            {/* cart UI left as-is/commented */}
          </div>
        </NamedLink>
      )}
    </>
  );
};

export default ListingCard;
